import Api from "api/Api";
import Button from "components/button/Button";
import React, { useEffect } from "react";
import { compose } from "redux";
import enhancer, { SCROLLER_RECOMMENDED } from "./validator";
import Select from "react-select";
import { FieldArray } from "formik";
import clsx from "clsx";

const SLIDE_TYPES = [
  { value: "IMAGE", label: "Image" },
  { value: "TEXT", label: "Text" },
  { value: "IMAGE_TEXT", label: "Image + Text" }
];

const AdvertisementForm = props => {
  const {
    handleBlur,
    errors,
    loading,
    touched,
    submitCount,
    onSubmit,
    values,
    action,
    setFieldValue,
    editedData,
    isStore,
    storeId,
    deviceCount,
    handleChange
  } = props;

  const [deviceList, setDeviceList] = React.useState([]);

  useEffect(() => {
    if (isStore) {
      Api.post(`/admin/store/devicelist?storeId=${storeId}`, {}).then(res => {
        setDeviceList(res.data.data);
      });
    } else {
      Api.get("/admin/device/deviceList", {}).then(res => {
        setDeviceList(res.data.data);
      });
    }
  }, [isStore, storeId]);

  const Error = ({ field, className }) => {
    const fieldError =
      errors[field] && (touched[field] || submitCount > 0)
        ? errors[field]
        : null;
    if (!fieldError) return <span />;
    return (
      <span className={className || "error-msg"}>
        {typeof fieldError === "string" ? fieldError : "Invalid value"}
      </span>
    );
  };

  const readImageDimensions = (file, index) => {
    const img = new Image();
    img.src = window.URL.createObjectURL(file);
    img.onload = () => {
      window.URL.revokeObjectURL(img.src);
      setFieldValue(`items.${index}.imageWidth`, img.naturalWidth);
      setFieldValue(`items.${index}.imageHeight`, img.naturalHeight);
    };
  };

  const handleImagePick = (e, index) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFieldValue(`items.${index}.file`, file);
    setFieldValue(`items.${index}.imageUrl`, URL.createObjectURL(file));
    readImageDimensions(file, index);
    e.target.value = "";
  };

  const handleSubmit = e => {
    e.preventDefault();
    const { isValid, handleSubmit: formikSubmit } = props;
    if (isValid) {
      onSubmit(values, action);
    }
    formikSubmit();
  };

  const previewSrc = item => {
    if (item.file) return URL.createObjectURL(item.file);
    return item.imageUrl || "";
  };

  return (
    <div>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label className="fs-16 medium-text">
            Device <span className="asterisk">*</span>
          </label>
          <Select
            name="device"
            isDisabled={Boolean(editedData)}
            placeholder="Select Device"
            value={values.device}
            isMulti={!editedData}
            onChange={data => setFieldValue("device", data)}
            onBlur={handleBlur}
            options={
              deviceList.length > 0
                ? deviceList.map(result => ({
                    label: (
                      <div>{`${result.email}(${result.storeName ??
                        "-"} - ${result.branchName ?? "-"})`}</div>
                    ),
                    value: result.email,
                    id: result.deviceTokenId
                  }))
                : []
            }
            styles={{ container: s => ({ ...s, zIndex: 999 }) }}
          />
          <Error field="device" />
        </div>

        <div
          className="mb-3 p-3"
          style={{
            background: "#f1f3ee",
            borderRadius: 8,
            border: "1px solid #e8ebe3"
          }}
        >
          <div className="fs-14 medium-text mb-1">Recommended image size</div>
          <div className="fs-13 text-muted">
            {SCROLLER_RECOMMENDED.width} × {SCROLLER_RECOMMENDED.height} px for
            the tablet top scroller strip. Actual uploaded dimensions are shown
            per slide.
          </div>
        </div>

        <label className="fs-16 medium-text d-block mb-2">
          Scroller slides <span className="asterisk">*</span>
        </label>

        <FieldArray name="items">
          {({ push, remove }) => (
            <div>
              {values.items.map((item, index) => (
                <div
                  key={index}
                  className="mb-4 p-3"
                  style={{
                    border: "1px solid #e8ebe3",
                    borderRadius: 10,
                    background: "#fff"
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <span className="fs-15 demi-bold-text">
                      Slide {index + 1}
                    </span>
                    {values.items.length > 1 && (
                      <Button
                        type="button"
                        className="c-btn c-danger form-button fs-14"
                        onClick={() => {
                          if (item.advertisementId) {
                            setFieldValue("deletedAdvertisementIds", [
                              ...(values.deletedAdvertisementIds || []),
                              item.advertisementId
                            ]);
                          }
                          remove(index);
                        }}
                      >
                        Remove
                      </Button>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="fs-14 medium-text">Slide type</label>
                    <select
                      className="form-control react-form-input"
                      name={`items.${index}.fileType`}
                      value={item.fileType}
                      onChange={e => {
                        setFieldValue(
                          `items.${index}.fileType`,
                          e.target.value
                        );
                        if (e.target.value === "TEXT") {
                          setFieldValue(`items.${index}.file`, null);
                          setFieldValue(`items.${index}.imageUrl`, "");
                        }
                      }}
                    >
                      {SLIDE_TYPES.map(t => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {(item.fileType === "TEXT" ||
                    item.fileType === "IMAGE_TEXT") && (
                    <div className="form-group">
                      <label className="fs-14 medium-text">
                        {item.fileType === "IMAGE_TEXT"
                          ? "Caption text"
                          : "Scroller text"}{" "}
                        <span className="asterisk">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control react-form-input"
                        name={`items.${index}.text`}
                        value={item.text}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder={
                          item.fileType === "IMAGE_TEXT"
                            ? "Text shown over the image"
                            : "Marquee text for the scroller"
                        }
                      />
                      {errors.items?.[index]?.text &&
                        (touched.items?.[index]?.text || submitCount > 0) && (
                          <span className="error-msg">
                            {errors.items[index].text}
                          </span>
                        )}
                    </div>
                  )}

                  {(item.fileType === "IMAGE" ||
                    item.fileType === "IMAGE_TEXT") && (
                    <>
                      <div className="form-group">
                        <label className="fs-14 medium-text">
                          Image <span className="asterisk">*</span>
                        </label>
                        <label className="c-btn c-secondary form-button fs-14 demi-bold-text">
                          {item.imageUrl || item.file
                            ? "Replace image"
                            : "Upload image"}
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/jpg"
                            style={{ display: "none" }}
                            onChange={e => handleImagePick(e, index)}
                          />
                        </label>
                      </div>

                      {(item.imageUrl || item.file) && (
                        <div className="mb-3">
                          <img
                            alt="Scroller preview"
                            src={previewSrc(item)}
                            style={{
                              width: Math.min(
                                item.imageWidth || SCROLLER_RECOMMENDED.width,
                                480
                              ),
                              maxWidth: "100%",
                              height: "auto",
                              borderRadius: 6,
                              border: "1px solid #e8ebe3",
                              background: "#404040"
                            }}
                          />
                          <div className="fs-13 text-muted mt-2">
                            Image size:{" "}
                            <strong>
                              {item.imageWidth || "—"} ×{" "}
                              {item.imageHeight || "—"}
                            </strong>{" "}
                            px
                            {item.imageWidth &&
                              item.imageHeight &&
                              (item.imageWidth !== SCROLLER_RECOMMENDED.width ||
                                item.imageHeight !==
                                  SCROLLER_RECOMMENDED.height) && (
                                <span className="text-warning ml-2">
                                  (recommended {SCROLLER_RECOMMENDED.width}×
                                  {SCROLLER_RECOMMENDED.height})
                                </span>
                              )}
                          </div>
                          {item.fileType === "IMAGE_TEXT" && item.text && (
                            <div
                              className="fs-14 medium-text mt-2 p-2 text-center"
                              style={{
                                background: "rgba(15,23,42,0.75)",
                                color: "#fff",
                                borderRadius: 4,
                                maxWidth: 480
                              }}
                            >
                              {item.text}
                            </div>
                          )}
                        </div>
                      )}

                      <div className="row">
                        <div className="col-md-6 form-group">
                          <label className="fs-14 medium-text">
                            Width (px)
                          </label>
                          <input
                            type="number"
                            min="1"
                            className="form-control react-form-input"
                            name={`items.${index}.imageWidth`}
                            value={item.imageWidth}
                            onChange={handleChange}
                          />
                        </div>
                        <div className="col-md-6 form-group">
                          <label className="fs-14 medium-text">
                            Height (px)
                          </label>
                          <input
                            type="number"
                            min="1"
                            className="form-control react-form-input"
                            name={`items.${index}.imageHeight`}
                            value={item.imageHeight}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ))}

              <Button
                type="button"
                className="c-btn form-button fs-14 demi-bold-text mb-3"
                onClick={() =>
                  push({
                    advertisementId: null,
                    fileType: "TEXT",
                    text: "",
                    imageUrl: "",
                    imageWidth: "",
                    imageHeight: "",
                    file: null
                  })
                }
              >
                + Add slide
              </Button>
            </div>
          )}
        </FieldArray>

        {typeof errors.items === "string" && submitCount > 0 && (
          <div className="error-msg mb-2">{errors.items}</div>
        )}

        {isStore && deviceCount === 0 && (
          <div className="mb-3">
            <Button
              className="c-btn mb-12"
              onClick={() => props.history.push("/store/device/add")}
              style={{ backgroundColor: "#0f172a", color: "#fff" }}
            >
              <i className="fas fa-plus mr-10" />
              Add Device
            </Button>
          </div>
        )}

        <Button
          type="submit"
          loading={loading}
          disabled={loading || (isStore && deviceCount === 0)}
          className={clsx(
            "c-btn c-info form-button fs-16 demi-bold-text mr-15"
          )}
          style={{ maxWidth: "125px" }}
          dataStyle="expand-right"
        >
          Submit
        </Button>
      </form>
    </div>
  );
};

export default compose(enhancer)(AdvertisementForm);
